import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as Immutable from "immutable";
import flatten from "lodash/flatten";
import isEqual from "lodash/isEqual";
import uniqWith from "lodash/uniqWith";
import { FormikContext } from "formik";
import styled, { css } from "styled-components";
import expandRows from "views/logic/ExpandRows";
import { defaultCompare } from "logic/DefaultCompare";
import WidgetContext from "views/components/contexts/WidgetContext";
import DataTableVisualizationConfig from "views/logic/aggregationbuilder/visualizations/DataTableVisualizationConfig";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { updateWidgetConfig } from "views/logic/slices/widgetActions";
import useWidgetUnits from "views/components/visualizations/hooks/useWidgetUnits";
import TableHead from "./TableHead";
import DataTableEntry from "./DataTableEntry";
import MessagesTable from "./MessagesTable";
import deduplicateValues from "./DeduplicateValues";
import Headers from "./Headers";
import styles from "./DataTable.css";
import useRowSelection from "./useRowSelection";
import SelectedRowsActions from "./SelectedRowsActions";
import RenderCompletionCallback from "../widgets/RenderCompletionCallback";
import { makeVisualization, retrieveChartData } from "../aggregationbuilder/AggregationBuilder";
const getPinnedColumnStyles = (tag, stickyLeftMarginsByColumnIndex) => css`
  ${stickyLeftMarginsByColumnIndex.map(
  ({ index, leftMargin }) => css`
      & ${tag}:nth-child(${index + 1}) {
        position: sticky !important;
        left: ${leftMargin}px;
        z-index: 1;
      }
    `
)}
`;
const THead = styled(TableHead)(
  ({ $stickyLeftMarginsByColumnIndex }) => css`
    & tr.pivot-header-row {
      ${getPinnedColumnStyles("th", $stickyLeftMarginsByColumnIndex)}
    }
  `
);
const TBody = styled.tbody(
  ({ $stickyLeftMarginsByColumnIndex }) => css`
    & tr {
      ${getPinnedColumnStyles("td", $stickyLeftMarginsByColumnIndex)}
    }
  `
);
const _compareArray = (ary1, ary2) => {
  if (ary1 === void 0) {
    if (ary2 === void 0) {
      return 0;
    }
    return -1;
  }
  if (ary1.length > ary2.length) {
    return 1;
  }
  if (ary1.length < ary2.length) {
    return -1;
  }
  const diffIdx = ary1.findIndex((v, idx) => defaultCompare(v, ary2[idx]) !== 0);
  if (diffIdx === -1) {
    return 0;
  }
  return defaultCompare(ary1[diffIdx], ary2[diffIdx]);
};
const _extractColumnPivotValues = (rows) => {
  const uniqRows = uniqWith(
    flatten(rows.filter(({ source }) => source === "leaf" || source === "non-leaf").map(({ values }) => values)).filter(({ rollup }) => !rollup).map(({ key }) => key.slice(0, -1)),
    isEqual
  );
  return Immutable.List(uniqRows).sort(_compareArray).toArray();
};
const DataTable = ({
  bordered = false,
  borderedHeader = true,
  condensed = true,
  config,
  data,
  editing,
  fields,
  setLoadingState,
  stickyHeader = true,
  striped = true
}) => {
  var _a, _b, _c, _d, _e, _f;
  const formContext = useContext(FormikContext);
  const onRenderComplete = useContext(RenderCompletionCallback);
  const widget = useContext(WidgetContext);
  useEffect(onRenderComplete, [onRenderComplete]);
  const [rowPivotColumnsWidth, setRowPivotColumnsWidth] = useState({});
  const dispatch = useViewsDispatch();
  const onSetColumnsWidth = useCallback(
    ({ field, offsetWidth }) => {
      setRowPivotColumnsWidth((cur) => {
        const copy = { ...cur };
        copy[field] = offsetWidth;
        return copy;
      });
    },
    [setRowPivotColumnsWidth]
  );
  const _onSortChange = useCallback(
    (newSort) => {
      const dirty = formContext == null ? void 0 : formContext.dirty;
      const updateWidget = () => dispatch(updateWidgetConfig(widget == null ? void 0 : widget.id, config.toBuilder().sort(newSort).build()));
      if (!editing || editing && !dirty) {
        return updateWidget();
      }
      if (window.confirm("You have unsaved changes in configuration form. This action will rollback them")) {
        return updateWidget();
      }
      return Promise.reject();
    },
    [formContext == null ? void 0 : formContext.dirty, editing, dispatch, widget == null ? void 0 : widget.id, config]
  );
  const togglePin = useCallback(
    (field) => {
      const dirty = formContext == null ? void 0 : formContext.dirty;
      const updateWidget = () => {
        var _a2, _b2;
        const curVisualizationConfig = (_a2 = widget == null ? void 0 : widget.config.visualizationConfig) != null ? _a2 : DataTableVisualizationConfig.create([]).toBuilder().build();
        const pinnedColumns2 = ((_b2 = curVisualizationConfig == null ? void 0 : curVisualizationConfig.pinnedColumns) == null ? void 0 : _b2.has(field)) ? curVisualizationConfig.pinnedColumns.delete(field) : curVisualizationConfig.pinnedColumns.add(field);
        return dispatch(
          updateWidgetConfig(
            widget == null ? void 0 : widget.id,
            widget == null ? void 0 : widget.config.toBuilder().visualizationConfig(curVisualizationConfig.toBuilder().pinnedColumns(pinnedColumns2.toJS()).build()).build()
          )
        );
      };
      if (!editing || editing && !dirty) {
        return updateWidget();
      }
      if (window.confirm("You have unsaved changes in configuration form. This action will rollback them")) {
        return updateWidget();
      }
      return Promise.reject();
    },
    [formContext == null ? void 0 : formContext.dirty, editing, widget == null ? void 0 : widget.config, widget == null ? void 0 : widget.id, dispatch]
  );
  const { columnPivots, rowPivots, series, rollupForBackendQuery: rollup, visualizationConfig } = config;
  const showRowNumbers = (_a = visualizationConfig == null ? void 0 : visualizationConfig.showRowNumbers) != null ? _a : true;
  const widgetUnits = useWidgetUnits(config);
  const rows = (_b = retrieveChartData(data)) != null ? _b : [];
  const rowFieldNames = rowPivots.flatMap((pivot) => pivot.fields);
  const columnFieldNames = columnPivots.flatMap((pivot) => pivot.fields);
  const seriesToMerge = rollup ? series : [];
  const effectiveFields = Immutable.OrderedSet(rowFieldNames.map((field) => ({ field, source: field }))).merge(
    seriesToMerge.map((s) => ({ field: s.effectiveName, source: s.function }))
  );
  const expandedRows = expandRows(
    rowFieldNames.slice(),
    columnFieldNames.slice(),
    rows.filter((r) => r.source === "leaf")
  );
  const rowSelection = useRowSelection(expandedRows, rowFieldNames, fields);
  const selectionColumnOffset = rowSelection.selectAll ? 1 : 0;
  const actualColumnPivotFields = _extractColumnPivotValues(rows);
  const pinnedColumns = useMemo(
    () => {
      var _a2, _b2, _c2;
      return (_c2 = (_b2 = (_a2 = widget == null ? void 0 : widget.config) == null ? void 0 : _a2.visualizationConfig) == null ? void 0 : _b2.pinnedColumns) != null ? _c2 : Immutable.Set();
    },
    [(_d = (_c = widget == null ? void 0 : widget.config) == null ? void 0 : _c.visualizationConfig) == null ? void 0 : _d.pinnedColumns]
  );
  const stickyLeftMarginsByColumnIndex = useMemo(() => {
    var _a2, _b2;
    let prev = 0;
    const res = [];
    const lineNumberOffset = (((_b2 = (_a2 = widget == null ? void 0 : widget.config) == null ? void 0 : _a2.visualizationConfig) == null ? void 0 : _b2.showRowNumbers) === false ? 0 : 1) + selectionColumnOffset;
    const rowPivotsFields = rowPivots.flatMap((rowPivot) => {
      var _a3;
      return (_a3 = rowPivot.fields) != null ? _a3 : [];
    });
    rowPivotsFields.forEach((field, index) => {
      if (pinnedColumns.has(field)) {
        const column = field;
        res.push({ index: index + lineNumberOffset, column, leftMargin: prev });
        prev += rowPivotColumnsWidth[field];
      }
    });
    series.forEach((row, index) => {
      if (pinnedColumns.has(row.function)) {
        const column = row.function;
        res.push({ index: index + rowPivots.length + lineNumberOffset, column, leftMargin: prev });
        prev += rowPivotColumnsWidth[row.function];
      }
    });
    return res;
  }, [
    (_f = (_e = widget == null ? void 0 : widget.config) == null ? void 0 : _e.visualizationConfig) == null ? void 0 : _f.showRowNumbers,
    selectionColumnOffset,
    rowPivots,
    series,
    pinnedColumns,
    rowPivotColumnsWidth
  ]);
  const pinnedColumnIndexes = useMemo(
    () => new Set(stickyLeftMarginsByColumnIndex.map(({ index }) => index)),
    [stickyLeftMarginsByColumnIndex]
  );
  const formattedRows = deduplicateValues(expandedRows, rowFieldNames).map((reducedItem, idx) => {
    const valuePath = rowFieldNames.map((pivotField) => ({ [pivotField]: expandedRows[idx][pivotField] }));
    const key = `datatableentry-${idx}`;
    return /* @__PURE__ */ React.createElement(
      DataTableEntry,
      {
        index: idx + 1,
        key,
        fields: effectiveFields,
        item: reducedItem,
        valuePath,
        columnPivots: columnFieldNames,
        columnPivotValues: actualColumnPivotFields,
        types: fields,
        series,
        showRowNumbers,
        units: widgetUnits,
        pinnedColumnIndexes,
        striped,
        selection: rowSelection.selectionFor(expandedRows[idx])
      }
    );
  });
  const sortConfigMap = useMemo(
    () => Immutable.OrderedMap(config.sort.map((sort) => [sort.field, sort])),
    [config]
  );
  return /* @__PURE__ */ React.createElement("div", { className: styles.container }, rowSelection.selectedRowsCount > 0 && /* @__PURE__ */ React.createElement(
    SelectedRowsActions,
    {
      selectedRowsCount: rowSelection.selectedRowsCount,
      fieldValues: rowSelection.selectedValuesByField,
      onClearSelection: rowSelection.clearSelection
    }
  ), /* @__PURE__ */ React.createElement("div", { className: styles.scrollContainer }, /* @__PURE__ */ React.createElement(MessagesTable, { striped, bordered, stickyHeader, condensed }, /* @__PURE__ */ React.createElement(THead, { $stickyLeftMarginsByColumnIndex: stickyLeftMarginsByColumnIndex }, /* @__PURE__ */ React.createElement(
    Headers,
    {
      actualColumnPivotFields,
      borderedHeader,
      columnPivots,
      fields,
      rollup,
      rowPivots,
      series,
      onSortChange: _onSortChange,
      sortConfigMap,
      onSetColumnsWidth,
      setLoadingState,
      pinnedColumns,
      pinnedColumnIndexes,
      togglePin,
      showRowNumbers,
      selectAll: rowSelection.selectAll
    }
  )), /* @__PURE__ */ React.createElement(TBody, { $stickyLeftMarginsByColumnIndex: stickyLeftMarginsByColumnIndex }, formattedRows))));
};
const ConnectedDataTable = makeVisualization(DataTable, "table");
export default ConnectedDataTable;
